<template>
  <div class="h-screen bg-background flex flex-col">
    <main class="flex-1 flex flex-col overflow-hidden">
      <!-- Authentication Forms with Airport Background -->
      <div
        v-if="!isAuthenticated"
        class="relative h-full bg-cover bg-center"
        :style="{ backgroundImage: `url(${require('@/assets/airport-background.jpg')})` }"
      >
        <!-- Blur overlay -->
        <div class="absolute inset-0 backdrop-blur-lg bg-black/40"></div>
        
        <!-- Content -->
        <div class="relative z-10 h-full flex items-center justify-center">
          <div class="max-w-7xl w-full px-4 sm:px-6 lg:px-8">
            <div class="flex items-center justify-center">
              <div class="flex items-center max-w-6xl w-full">
                <!-- Welcome Text Section (Left Side) -->
                <div class="w-1/2 pr-8">
                  <div class="mb-6">
                    <img src="./assets/Fraport_Logo_Slovenija.svg" alt="Fraport Slovenija Logo" class="h-24 w-auto invert brightness-0 filter">
                  </div>
                  <h1 class="text-4xl font-bold text-white mb-4 text-shadow">Welcome to Fraport Insights - Your Intelligent Airport Management Solution</h1>
                  <p class="text-xl text-white text-shadow">Streamline airport operations with advanced forecasting and AI-powered workflow optimization.</p>
                </div>
                
                <!-- Form Section (Right Side) -->
                <div class="w-1/2 flex justify-center">
                  <div class="w-full max-w-md">
                    <LoginForm
                      v-if="showLoginForm"
                      @login-success="handleLoginSuccess"
                      @switch-to-register="showRegisterForm = true; showLoginForm = false"
                    />
                    <RegisterForm
                      v-else
                      @register-success="handleRegisterSuccess"
                      @switch-to-login="showRegisterForm = false; showLoginForm = true"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Main Content with Side Navigation (shown after login) -->
      <div v-else class="flex h-full">
        <!-- Side Navigation -->
        <SideNav
          :activeItem="activeNavItem"
          :userData="userData"
          @navigate="handleNavigation"
          @logout="logout"
          @toggle-collapse="handleSideNavCollapse"
        />
        
        <!-- Content Area -->
        <div class="flex-1 overflow-auto p-6 transition-all duration-300 bg-gray-50">
          <Dashboard v-if="activeNavItem === 'dashboard'" />
          <EmailGenerator v-else-if="activeNavItem === 'email-generator'" />
          <UserSettings
            v-else-if="activeNavItem === 'user-settings'"
            :userData="userData"
            @update-user-data="updateUserData"
          />
          <SupabaseTest v-else-if="activeNavItem === 'supabase-test'" />
          <StorageTest v-else-if="activeNavItem === 'storage-test'" />
          <ForecastingPCO v-else-if="activeNavItem === 'forecasting-pco'" />
          <Administration
            v-else-if="activeNavItem === 'administration'"
            :initialView="administrationSubView"
          />
          <ForecastingPlanners v-else-if="activeNavItem === 'forecasting-planners'" />
          <TemplateStorageExample v-else-if="activeNavItem === 'template-storage'" />
        </div>
      </div>
    </main>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { logger } from './services/logger';
import LoginForm from './components/LoginForm.vue';
import RegisterForm from './components/RegisterForm.vue';
import Dashboard from './components/Dashboard.vue';
import SideNav from './components/SideNav.vue';
import { EmailGenerator } from './features/email-generation/components'
import SupabaseTest from './components/SupabaseTest.vue';
import StorageTest from './components/StorageTest.vue';
import UserSettings from './components/UserSettings.vue';
import ForecastingPCO from './components/ForecastingPCO.vue';
import ForecastingPlanners from './components/ForecastingPlanners.vue';
import Administration from './components/Administration.vue';
import TemplateStorageExample from './features/template-storage/examples/TemplateStorageExample.vue';
import { supabase } from './services/supabase';
import { useAuth } from './composables/useAuth';
import { useNavigation } from './composables/useNavigation';
import { SystemRole } from './types/systemRole';
import { BusinessRole } from './types/businessRole';

export default defineComponent({
  name: 'App',
  components: {
    LoginForm,
    RegisterForm,
    Dashboard,
    SideNav,
    EmailGenerator,
    SupabaseTest,
    StorageTest,
    UserSettings,
    ForecastingPCO,
    ForecastingPlanners,
    Administration,
    TemplateStorageExample
  },
  setup() {
    const { user, profile, isAuthenticated, logout: authLogout, init } = useAuth();
    const { activeNavItem, administrationSubView } = useNavigation();
    
    const showLoginForm = ref(true);
    const showRegisterForm = ref(false);
    const sideNavCollapsed = ref(false);
    
    // Computed property to format user data for components
    const userData = computed(() => {
      if (!user.value || !profile.value) {
        return {
          name: '',
          email: '',
          username: '',
          system_role: SystemRole.USER,
          avatar: null,
          business_role: BusinessRole.PLANNER // Default to PLANNER if no profile
        };
      }
      // Ensure business_role is a valid enum value
      let businessRole = profile.value.business_role as string;
      
      // Check if the business_role is one of the valid enum values
      const validRoles = Object.values(BusinessRole);
      const isValidRole = businessRole && validRoles.includes(businessRole as any);
      
      if (!isValidRole) {
        businessRole = BusinessRole.PLANNER;
      }
            
      // Determine the system role
      let systemRole = SystemRole.USER;
      
      // First check if system_role is already set in the profile
      if (profile.value.system_role) {
       
        // Check if it's a string that matches an enum value
        if (typeof profile.value.system_role === 'string') {
          const sysRoleLower = profile.value.system_role.toLowerCase();
          
          if (sysRoleLower === 'administrator' || sysRoleLower === 'admin') {
            systemRole = SystemRole.ADMIN;
          } else if (sysRoleLower === 'user') {
            systemRole = SystemRole.USER;
          }
        }
      }
      // If no system_role, fall back to role
      else if (profile.value.role) {
        
        if (profile.value.role === 'Administrator') {
          systemRole = SystemRole.ADMIN;
        } else if (profile.value.role === 'User') {
          systemRole = SystemRole.USER;
        } else {
          // Try case-insensitive comparison
          const roleLower = profile.value.role.toLowerCase();
          
          if (roleLower === 'administrator') {
            systemRole = SystemRole.ADMIN;
          } else {
            systemRole = SystemRole.USER;
          }
        }
      }
      
      
      return {
        name: profile.value.name || '',
        email: user.value.email || '',
        username: profile.value.username || '',
        system_role: systemRole,
        avatar: profile.value.avatar_url,
        business_role: businessRole
      };
    });
    
    // Initialize auth state when component is mounted
    onMounted(async () => {
      try {
        
        // Force a refresh of the auth state
        const { data } = await supabase.auth.getSession();
        
        if (data.session) {
          // If we have a session but it's about to expire, refresh it
          const expiresAt = data.session.expires_at;
          const expiresIn = expiresAt ? new Date(expiresAt * 1000).getTime() - Date.now() : 0;
          
          if (expiresIn < 60 * 60 * 1000) { // Less than 1 hour remaining
            await supabase.auth.refreshSession();
          }
        }
        
        // Initialize auth state
        await init();
        
        // Set up a periodic check to refresh the token if needed
        const checkInterval = setInterval(async () => {
          const { data } = await supabase.auth.getSession();
          if (data.session) {
            const expiresAt = data.session.expires_at;
            const expiresIn = expiresAt ? new Date(expiresAt * 1000).getTime() - Date.now() : 0;
            
            if (expiresIn < 60 * 60 * 1000) { // Less than 1 hour remaining
              await supabase.auth.refreshSession();
            }
          }
        }, 15 * 60 * 1000); // Check every 15 minutes
        
        // Clean up interval on component unmount
        return () => clearInterval(checkInterval);
      } catch (err) {
        logger.error('Error initializing auth state', { error: err });
      }
    });
    
    function handleLoginSuccess() {
      activeNavItem.value = 'dashboard';
    }
    
    function handleRegisterSuccess() {
      activeNavItem.value = 'dashboard';
    }
    
    async function logout() {
      try {
        // Call the enhanced logout function from useAuth
        await authLogout();
        
        // Reset UI state
        activeNavItem.value = 'dashboard';
        showLoginForm.value = true;
        showRegisterForm.value = false;
        
      } catch (err) {
        // If logout fails, try a more aggressive approach
        logger.error('error logging out' + err)
        window.location.href = '/';
      }
    }
    
    function handleNavigation(navItem: string) {
      activeNavItem.value = navItem;
    }
    
    function handleSideNavCollapse(isCollapsed: boolean) {
      sideNavCollapsed.value = isCollapsed;
    }
    
    return {
      isAuthenticated,
      showLoginForm,
      showRegisterForm,
      userData,
      activeNavItem,
      sideNavCollapsed,
      administrationSubView,
      handleLoginSuccess,
      handleRegisterSuccess,
      logout,
      handleNavigation,
      handleSideNavCollapse
    };
  }
});
</script>

<style>
body {
  margin: 0;
  padding: 0;
}
</style>
